import type {
  WebArViewPort
} from "./controller.js";
import type { WebArFailureCode, WebArState } from "./contracts.js";

const RETRYABLE_FAILURES = new Set<WebArFailureCode>([
  "launch_rejected",
  "camera_denied",
  "engine_load_failed",
  "model_load_failed",
  "runtime_failed",
  "cancelled"
]);

const FAILURE_MESSAGES: Partial<Record<WebArFailureCode, string>> = {
  camera_denied: "Camera access is required. Allow it, then try again.",
  unsupported: "This browser cannot start AR. Return to the 3D view.",
  expired: "This AR session expired. Return to the product and open AR again.",
  launch_replayed: "This AR session was already used. Return to the product and open AR again.",
  model_integrity_failed: "The released 3D model could not be verified.",
  runtime_failed: "AR stopped unexpectedly. Try again or return to the 3D view."
};

export function createDomWebArView(root: HTMLElement): WebArViewPort {
  root.innerHTML = `
    <main class="ar-shell" data-state="idle">
      <canvas class="ar-camera" aria-label="Live augmented reality view"></canvas>
      <header class="ar-header">
        <button class="icon-button ar-exit" type="button" aria-label="Return to 3D configurator">
          <span aria-hidden="true">×</span>
        </button>
      </header>
      <div class="surface-reticle" aria-hidden="true"><span></span></div>
      <section class="ar-preflight" aria-label="AR startup status">
        <span class="tracking-mark" aria-hidden="true"></span>
        <p class="ar-status" role="status" aria-live="polite"></p>
        <button class="primary-action ar-start" type="button" hidden>Try again</button>
      </section>
      <section class="ar-controls" aria-label="Object placement controls">
        <button class="control-button ar-reposition" type="button">
          <span aria-hidden="true">⌖</span> Place again
        </button>
        <label class="scale-control">
          <span>Scale</span>
          <input class="ar-scale" type="range" aria-label="Product scale">
        </label>
      </section>
      <aside class="engine-notice" aria-label="AR engine legal notice">
        <span>8th Wall Engine by Niantic Spatial, Inc. · Copyright © 2026 ·</span>
        <a href="https://github.com/8thwall/engine/blob/main/LICENSE" target="_blank" rel="noreferrer">License</a>
        <span>· “AS IS”</span>
      </aside>
    </main>
  `;

  const shell = root.querySelector<HTMLElement>(".ar-shell")!;
  const canvas = root.querySelector<HTMLCanvasElement>(".ar-camera")!;
  const status = root.querySelector<HTMLElement>(".ar-status")!;
  const start = root.querySelector<HTMLButtonElement>(".ar-start")!;
  const exit = root.querySelector<HTMLButtonElement>(".ar-exit")!;
  const reposition = root.querySelector<HTMLButtonElement>(".ar-reposition")!;
  const scale = root.querySelector<HTMLInputElement>(".ar-scale")!;
  const listeners: Array<() => void> = [];

  const bind = <EventType extends Event>(
    target: EventTarget,
    name: string,
    listener: (event: EventType) => void
  ) => {
    const handler = listener as EventListener;
    target.addEventListener(name, handler);
    const unbind = () => target.removeEventListener(name, handler);
    listeners.push(unbind);
    return unbind;
  };

  return {
    canvas,
    bindStart: (listener) => bind<MouseEvent>(start, "click", listener),
    bindExit: (listener) => bind<MouseEvent>(exit, "click", listener),
    bindScale: (listener) => bind<InputEvent>(scale, "input", () => {
      scale.setAttribute("aria-valuetext", `${scale.value}×`);
      listener(Number(scale.value));
    }),
    bindReposition: (listener) => bind<MouseEvent>(reposition, "click", listener),
    setScale(value) {
      scale.value = String(value);
      scale.setAttribute("aria-valuetext", `${value}×`);
    },
    render(input) {
      shell.dataset.state = input.state;
      status.textContent =
        input.state === "error" && input.errorCode !== undefined
          ? (FAILURE_MESSAGES[input.errorCode] ?? input.message)
          : input.message;
      status.setAttribute("role", input.state === "error" ? "alert" : "status");
      const busy = !["idle", "active", "error", "exited"].includes(input.state);
      start.disabled = busy;
      start.setAttribute("aria-busy", String(busy));
      if (input.state === "error") {
        const retryable = input.errorCode !== undefined && RETRYABLE_FAILURES.has(input.errorCode);
        start.hidden = !retryable;
        start.textContent = input.errorCode === "camera_denied" ? "Enable camera" : "Try again";
      } else {
        start.hidden = true;
      }
      if (input.scale) {
        scale.min = String(input.scale.minimum);
        scale.max = String(input.scale.maximum);
        scale.step = String(input.scale.step);
        scale.value = String(input.scale.initial);
        scale.setAttribute("aria-valuetext", `${input.scale.initial}×`);
      }
    },
    dispose() {
      for (const unbind of listeners.splice(0)) unbind();
      root.replaceChildren();
    }
  };
}
